export const createReceiptFieldFormatter = (options = {}) => {
  const {
    formatCurrency = (amount) =>
      new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0),
    formatDate = (value) => {
      if (!value) return value
      const date = new Date(value)
      return isNaN(date.getTime()) ? value : date.toLocaleDateString('de-DE')
    },
    formatDateTime = (value) => {
      if (!value) return value
      const date = new Date(value)
      return isNaN(date.getTime()) ? value : date.toLocaleString('de-DE')
    },
    isFinancialField = () => false,
    formatRepeaterFieldValue = (value) => value,
    formatTaxRateValue = (value) => value,
  } = options

  const isEmpty = (val) => val === '' || val === null || val === undefined

  const toNumber = (val) => {
    if (typeof val === 'number') return val
    if (typeof val === 'string' && val.trim() !== '') {
      const normalized = val.trim().replace(/\s+/g, '')
      if (normalized.includes(',') && normalized.includes('.')) {
        return parseFloat(normalized.replace(/\./g, '').replace(',', '.'))
      }
      if (normalized.includes(',')) {
        return parseFloat(normalized.replace(',', '.'))
      }
      return parseFloat(normalized)
    }
    return NaN
  }

  const isNumeric = (val) => !isNaN(toNumber(val))

  const formatBoolean = (val) => {
    if (typeof val === 'boolean') {
      return val ? 'Ja' : 'Nein'
    }
    if (typeof val === 'number') {
      if (val === 1) return 'Ja'
      if (val === 0) return 'Nein'
    }
    if (typeof val === 'string') {
      const normalized = val.trim().toLowerCase()
      if (['true', '1', 'yes', 'ja', 'on'].includes(normalized)) return 'Ja'
      if (['false', '0', 'no', 'nein', 'off'].includes(normalized)) return 'Nein'
    }
    return null
  }

  const formatByType = (dataValue, fieldMeta, fieldName) => {
    const fieldType = fieldMeta?.type ? String(fieldMeta.type).toLowerCase() : null
    if (!fieldType) return undefined

    switch (fieldType) {
      case 'date': {
        const date = new Date(dataValue)
        return isNaN(date.getTime()) ? dataValue : formatDate(dataValue)
      }
      case 'datetime':
      case 'date_time':
      case 'datetime-local':
      case 'datetime_local': {
        const date = new Date(dataValue)
        return isNaN(date.getTime()) ? dataValue : formatDateTime(dataValue)
      }
      case 'time':
        return dataValue
      case 'boolean':
      case 'checkbox':
      case 'toggle': {
        const formatted = formatBoolean(dataValue)
        return formatted !== null ? formatted : dataValue
      }
      case 'repeater':
        return formatRepeaterFieldValue(fieldMeta ?? dataValue, fieldName, fieldMeta)
      case 'money':
      case 'currency':
      case 'financial':
        if (isNumeric(dataValue)) {
          const numeric = toNumber(dataValue)
          return isNaN(numeric) ? dataValue : formatCurrency(numeric)
        }
        return dataValue
      case 'number':
      case 'integer':
      case 'decimal':
        if (isNumeric(dataValue)) {
          const numeric = toNumber(dataValue)
          if (isNaN(numeric)) return dataValue
          if (isFinancialField(fieldName, fieldMeta)) {
            return formatCurrency(numeric)
          }
          return numeric
        }
        return dataValue
      case 'percentage':
        if (isNumeric(dataValue)) {
          const numeric = toNumber(dataValue)
          return isNaN(numeric) ? dataValue : `${numeric.toFixed(2)} %`
        }
        return dataValue
      case 'tax_rate':
      case 'taxrate':
        return formatTaxRateValue(fieldMeta?.resolvedTaxRate || dataValue, fieldName, fieldMeta)
      case 'json':
        return typeof dataValue === 'string' ? dataValue : JSON.stringify(dataValue)
      default:
        return undefined
    }
  }

  const formatFieldValue = (value, fieldName = null, fieldMetaOverride = null) => {
    const meta =
      fieldMetaOverride ??
      (typeof value === 'object' && value !== null && Object.prototype.hasOwnProperty.call(value, 'data')
        ? value
        : null)

    if (meta) {
      const dataValue = meta.data
      if (isEmpty(dataValue)) {
        return '-'
      }

      const formattedByType = formatByType(dataValue, meta, fieldName)
      if (formattedByType !== undefined) {
        return formattedByType
      }

      if (Array.isArray(dataValue)) {
        return formatRepeaterFieldValue(meta, fieldName, meta)
      }

      const booleanDisplay = formatBoolean(dataValue)
      if (booleanDisplay !== null) {
        return booleanDisplay
      }

      if (isFinancialField(fieldName, meta) && isNumeric(dataValue)) {
        const numeric = toNumber(dataValue)
        return isNaN(numeric) ? dataValue : formatCurrency(numeric)
      }

      if (typeof dataValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dataValue)) {
        const date = new Date(dataValue)
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('de-DE')
        }
      }

      if (typeof dataValue === 'object') {
        return JSON.stringify(dataValue)
      }

      return dataValue
    }

    const booleanDisplay = formatBoolean(value)
    if (booleanDisplay !== null) {
      return booleanDisplay
    }

    if (value && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const date = new Date(value)
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('de-DE')
      }
      return value
    }

    if (fieldName && fieldName.endsWith('_taxRate')) {
      return formatTaxRateValue(value, fieldName, fieldMetaOverride)
    }

    if (Array.isArray(value)) {
      return formatRepeaterFieldValue(value, fieldName, fieldMetaOverride)
    }

    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value)
    }

    if (isFinancialField(fieldName, fieldMetaOverride) && isNumeric(value)) {
      const numeric = toNumber(value)
      return isNaN(numeric) ? value : formatCurrency(numeric)
    }

    return value
  }

  return { formatFieldValue }
}

/**
 * Berechnet die relative Luminanz einer Farbe (nach WCAG-Standard)
 * @param {string} hexColor - Hex-Farbcode (z.B. "#000000" oder "000000")
 * @returns {number} Relative Luminanz zwischen 0 (schwarz) und 1 (weiß)
 */
const getRelativeLuminance = (hexColor) => {
  if (!hexColor || typeof hexColor !== 'string') {
    return 0.5 // Fallback: mittlere Luminanz
  }

  let normalized = hexColor.replace('#', '').trim()

  // 3-stellige Hex-Codes zu 6-stelligen erweitern
  if (normalized.length === 3) {
    normalized = normalized
      .split('')
      .map((char) => char + char)
      .join('')
  }

  if (normalized.length !== 6) {
    return 0.5 // Fallback
  }

  const r = parseInt(normalized.substring(0, 2), 16) / 255
  const g = parseInt(normalized.substring(2, 4), 16) / 255
  const b = parseInt(normalized.substring(4, 6), 16) / 255

  // Linearisierung (Gamma-Korrektur)
  const linearize = (component) => {
    return component <= 0.03928
      ? component / 12.92
      : Math.pow((component + 0.055) / 1.055, 2.4)
  }

  const rLinear = linearize(r)
  const gLinear = linearize(g)
  const bLinear = linearize(b)

  // Relative Luminanz nach WCAG
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear
}

/**
 * Bestimmt die passende Textklasse basierend auf der Hintergrundfarbe
 * @param {string} backgroundColor - Hex-Farbcode (z.B. "#000000" oder "000000")
 * @returns {string} "text-dark" für helle Hintergründe, "text-light" für dunkle
 */
export const getContrastTextClass = (backgroundColor) => {
  const luminance = getRelativeLuminance(backgroundColor)
  // Wenn Luminanz größer als 0.5 (hell), dann dunkler Text, sonst heller Text
  return luminance > 0.5 ? 'text-dark' : 'text-light'
}

// Helper: Farbe -> RGBA mit Alpha (wie im Wizard)
export const hexToRgba = (hex, alpha = 0.1) => {
  if (!hex || typeof hex !== 'string') {
    return `rgba(215, 16, 33, ${alpha})`
  }
  let normalized = hex.replace('#', '').trim()
  if (normalized.length === 3) {
    normalized = normalized
      .split('')
      .map((char) => char + char)
      .join('')
  }
  if (normalized.length !== 6) {
    return `rgba(215, 16, 33, ${alpha})`
  }
  const intVal = parseInt(normalized, 16)
  const r = (intVal >> 16) & 255
  const g = (intVal >> 8) & 255
  const b = intVal & 255
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// Gleiche Block-Optik wie im Wizard
export const getGroupBlockStyle = (color) => {
  const fallbackColor = color || '#D71021'
  return {
    borderLeft: `6px solid ${fallbackColor}`,
    background: hexToRgba(fallbackColor, 0.08),
    borderRadius: '14px',
  }
}

export const groupFieldsByGroupId = (
  fieldsByKey = {},
  groupSettings = [],
  options = {}
) => {
  const {
    getGroupId = (fieldInfo) =>
      fieldInfo?.groupId ?? fieldInfo?.value?.groupId ?? null,
    filter = () => true,
    transformField = ({ key, fieldInfo, display }) => ({ key, fieldInfo, display }),
    getDisplay,
  } = options

  const groupMap = new Map(
    (groupSettings || []).map((group) => [
      group.id,
      {
        ...group,
        fields: [],
      },
    ])
  )

  const ungrouped = []

  Object.entries(fieldsByKey || {}).forEach(([key, fieldInfo]) => {
    if (!fieldInfo) return
    const display =
      typeof getDisplay === 'function' ? getDisplay(fieldInfo, key) : undefined

    if (filter && filter(fieldInfo, key, display) === false) {
      return
    }

    const groupId = getGroupId(fieldInfo, key)

    const entry = transformField({ key, fieldInfo, display })

    if (groupId && groupMap.has(groupId)) {
      groupMap.get(groupId).fields.push(entry)
    } else {
      ungrouped.push(entry)
    }
  })

  const groups = Array.from(groupMap.values()).filter(
    (group) => Array.isArray(group.fields) && group.fields.length > 0
  )

  return {
    groups,
    ungrouped,
  }
}
